/* ============================================================
   Hand-drawn SVG charts.

   No charting library. The identity fixes three things a library
   has to be fought over: the series palette is a validated set
   assigned in a fixed order and never cycled, grid lines are solid
   hairlines and never dashed, and colour is never the only carrier
   of meaning. These primitives obey all three by construction and
   weigh nothing in the bundle.

   Planned and actual are drawn differently on purpose. Hugin is
   thought — the plan — and gets the thin line. Munin is memory —
   what actually happened — and gets the solid weight. The distance
   between them is the finding, so the eye must be able to see it
   without reading a legend.
   ============================================================ */

const PAD = { l: 46, r: 14, t: 14, b: 28 };

export type Series = {
  name: string;
  /** May be shorter than `labels` — an actual curve stops at today. */
  points: number[];
  /** A CSS custom property name, e.g. 'var(--series-1)'. */
  color: string;
  /** 'actual' draws heavy with a fill, 'planned' draws thin. */
  kind?: 'actual' | 'planned';
};

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const step = [1, 2, 2.5, 5, 10].find((s) => v <= s * mag) ?? 10;
  return step * mag;
}

function fmt(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v * 10) / 10);
}

/* ------------------------------------------------------------
   Line / area chart
   ------------------------------------------------------------ */

export function Curve({
  series,
  labels,
  height = 220,
  suffix = '',
}: {
  series: Series[];
  labels: string[];
  height?: number;
  suffix?: string;
}) {
  const W = 640;
  const H = height;
  const peak = Math.max(...series.flatMap((s) => s.points), 0);
  const max = niceMax(peak);
  const n = Math.max(labels.length, 2);

  const x = (i: number) => PAD.l + (i * (W - PAD.l - PAD.r)) / (n - 1);
  const y = (v: number) => H - PAD.b - (v / max) * (H - PAD.t - PAD.b);

  const line = (pts: number[]) =>
    pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');

  const area = (pts: number[]) =>
    `${line(pts)} L ${x(pts.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  // Labels every nth so they never collide at narrow widths.
  const every = Math.ceil(n / 8);

  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" className="chart-svg">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} className="grid-line" />
            <text x={PAD.l - 8} y={y(t) + 4} className="axis" textAnchor="end">
              {fmt(t)}
            </text>
          </g>
        ))}

        {labels.map((l, i) =>
          i % every === 0 ? (
            <text key={l + i} x={x(i)} y={H - 8} className="axis" textAnchor="middle">
              {l}
            </text>
          ) : null,
        )}

        {series.map((s) =>
          s.kind === 'actual' && s.points.length > 1 ? (
            <path key={`${s.name}-fill`} d={area(s.points)} fill={s.color} opacity={0.1} />
          ) : null,
        )}

        {series.map((s) => (
          <path
            key={s.name}
            d={line(s.points)}
            fill="none"
            stroke={s.color}
            strokeWidth={s.kind === 'planned' ? 1.5 : 2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={s.kind === 'planned' ? 0.85 : 1}
          />
        ))}

        {/* The last actual reading is the one people look for. */}
        {series
          .filter((s) => s.kind === 'actual' && s.points.length > 0)
          .map((s) => (
            <circle
              key={`${s.name}-dot`}
              cx={x(s.points.length - 1)}
              cy={y(s.points[s.points.length - 1]!)}
              r={4}
              fill={s.color}
              stroke="var(--bg-surface)"
              strokeWidth={2}
            />
          ))}
      </svg>

      <Legend
        items={series.map((s) => ({
          name: s.name,
          color: s.color,
          shape: s.kind === 'planned' ? ('thin' as const) : ('thick' as const),
          value:
            s.points.length > 0 ? `${fmt(s.points[s.points.length - 1]!)}${suffix}` : undefined,
        }))}
      />
    </figure>
  );
}

/* ------------------------------------------------------------
   Horizontal bars, optionally paired
   ------------------------------------------------------------ */

export type BarRow = {
  label: string;
  value: number;
  /** Drawn behind `value` — a budget, a plan, a reported figure. */
  against?: number;
  tone?: 'accent' | 'good' | 'warn' | 'crit' | 'muted';
  /** Overrides `tone` — for ordered rows that need the ordinal ramp. */
  color?: string;
};

const TONE: Record<string, string> = {
  accent: 'var(--accent)',
  good: 'var(--status-good)',
  warn: 'var(--status-warning)',
  crit: 'var(--status-critical)',
  muted: 'var(--text-muted)',
};

export function BarsH({
  rows,
  suffix = '',
  againstLabel,
  valueLabel,
}: {
  rows: BarRow[];
  suffix?: string;
  againstLabel?: string;
  valueLabel?: string;
}) {
  const max = niceMax(Math.max(...rows.flatMap((r) => [r.value, r.against ?? 0])));

  return (
    <div className="barsh">
      {againstLabel && valueLabel && (
        <div className="barsh-key">
          <span>
            <i className="key-box key-ghost" /> {againstLabel}
          </span>
          <span>
            <i className="key-box" style={{ background: 'var(--accent)' }} /> {valueLabel}
          </span>
        </div>
      )}
      {rows.map((r) => (
        <div className="barh" key={r.label}>
          <span className="barh-label" title={r.label}>
            {r.label}
          </span>
          <span className="barh-track">
            {r.against != null && (
              <i className="barh-ghost" style={{ width: `${(r.against / max) * 100}%` }} />
            )}
            <i
              className="barh-fill"
              style={{
                width: `${(r.value / max) * 100}%`,
                background: r.color ?? TONE[r.tone ?? 'accent'],
              }}
            />
          </span>
          <span className="barh-value">
            {fmt(r.value)}
            {suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------
   Vertical bars, optionally paired (in / out, planned / actual)
   ------------------------------------------------------------ */

export function BarsV({
  labels,
  a,
  b,
  aName,
  bName,
  height = 200,
}: {
  labels: string[];
  a: number[];
  b?: number[];
  aName: string;
  bName?: string;
  height?: number;
}) {
  const W = 640;
  const H = height;
  const max = niceMax(Math.max(...a, ...(b ?? [0])));
  const n = labels.length;
  const slot = (W - PAD.l - PAD.r) / n;
  const paired = b != null;
  const bw = Math.max(3, (slot * 0.62) / (paired ? 2 : 1));

  const y = (v: number) => H - PAD.b - (v / max) * (H - PAD.t - PAD.b);
  const every = Math.ceil(n / 10);

  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" className="chart-svg">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(f * max)} y2={y(f * max)} className="grid-line" />
            <text x={PAD.l - 8} y={y(f * max) + 4} className="axis" textAnchor="end">
              {fmt(f * max)}
            </text>
          </g>
        ))}

        {labels.map((l, i) => {
          const cx = PAD.l + i * slot + slot / 2;
          const x0 = paired ? cx - bw : cx - bw / 2;
          return (
            <g key={l + i}>
              <rect
                x={x0}
                y={y(a[i] ?? 0)}
                width={bw}
                height={Math.max(0, y(0) - y(a[i] ?? 0))}
                fill="var(--series-1)"
                rx={2}
              />
              {paired && (
                <rect
                  x={cx}
                  y={y(b![i] ?? 0)}
                  width={bw}
                  height={Math.max(0, y(0) - y(b![i] ?? 0))}
                  fill="var(--series-2)"
                  rx={2}
                />
              )}
              {i % every === 0 && (
                <text x={cx} y={H - 8} className="axis" textAnchor="middle">
                  {l}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <Legend
        items={[
          { name: aName, color: 'var(--series-1)', shape: 'box' },
          ...(bName ? [{ name: bName, color: 'var(--series-2)', shape: 'box' as const }] : []),
        ]}
      />
    </figure>
  );
}

/* ------------------------------------------------------------
   Donut
   ------------------------------------------------------------ */

export type Slice = { name: string; value: number; color: string };

export function Donut({ slices, centre, centreNote }: { slices: Slice[]; centre: string; centreNote?: string }) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  const R = 62;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <figure className="chart donut-wrap">
      <svg viewBox="0 0 170 170" role="img" className="donut-svg">
        <g transform="translate(85,85) rotate(-90)">
          <circle r={R} fill="none" stroke="var(--bg-elevated)" strokeWidth={22} />
          {slices.map((s) => {
            const len = (s.value / total) * C;
            const el = (
              <circle
                key={s.name}
                r={R}
                fill="none"
                stroke={s.color}
                strokeWidth={22}
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </g>
        <text x={85} y={82} textAnchor="middle" className="donut-centre">
          {centre}
        </text>
        {centreNote && (
          <text x={85} y={100} textAnchor="middle" className="donut-note">
            {centreNote}
          </text>
        )}
      </svg>

      <Legend
        items={slices.map((s) => ({
          name: s.name,
          color: s.color,
          shape: 'box' as const,
          value: `${Math.round((s.value / total) * 100)}%`,
        }))}
        stacked
      />
    </figure>
  );
}

/* ------------------------------------------------------------
   One 100% stacked bar
   ------------------------------------------------------------ */

export function Stack100({ slices, suffix = '' }: { slices: Slice[]; suffix?: string }) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="stack-wrap">
      <div className="stack100">
        {slices.map((s) => (
          <i
            key={s.name}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
            title={`${s.name}: ${s.value}`}
          />
        ))}
      </div>
      <Legend
        items={slices.map((s) => ({
          name: s.name,
          color: s.color,
          shape: 'box' as const,
          value: `${s.value}${suffix}`,
        }))}
      />
    </div>
  );
}

/* ------------------------------------------------------------
   Legend — shape carries meaning as well as colour
   ------------------------------------------------------------ */

function Legend({
  items,
  stacked,
}: {
  items: { name: string; color: string; shape: 'box' | 'thick' | 'thin'; value?: string }[];
  stacked?: boolean;
}) {
  return (
    <figcaption className={stacked ? 'legend legend-stacked' : 'legend'}>
      {items.map((it) => (
        <span key={it.name} className="legend-item">
          <i
            className={`key-box key-${it.shape}`}
            style={it.shape === 'box' ? { background: it.color } : { borderColor: it.color }}
          />
          <span className="legend-name">{it.name}</span>
          {it.value && <b>{it.value}</b>}
        </span>
      ))}
    </figcaption>
  );
}

/* ------------------------------------------------------------
   KPI row
   ------------------------------------------------------------ */

export type Kpi = {
  label: string;
  value: string;
  note?: string;
  tone?: 'good' | 'warn' | 'crit';
};

export function Kpis({ items }: { items: Kpi[] }) {
  return (
    <div className="kpis">
      {items.map((k) => (
        <div className="kpi" key={k.label}>
          <span className="kpi-label">{k.label}</span>
          <span className="kpi-value">{k.value}</span>
          {k.note && <span className={`kpi-note${k.tone ? ` ${k.tone}` : ''}`}>{k.note}</span>}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------
   Panel — the frame every chart sits in
   ------------------------------------------------------------ */

export function Panel({
  title,
  note,
  wide,
  children,
}: {
  title: string;
  note?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={wide ? 'panel panel-wide' : 'panel'}>
      <header className="panel-head">
        <h3>{title}</h3>
        {note && <p>{note}</p>}
      </header>
      {children}
    </section>
  );
}
